import React from 'react';
import moment, { Moment } from 'moment-timezone';
import type { Period } from '@spike/model';

import styles from './RoomsOperatingTable.module.scss';

import { Table } from 'components/UI/V3/Table/Table';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';

import { Label } from 'components/UI/V3/Forms/Label/Label';
import Button from 'components/UI/V3/Button/Button';
import { Spinner } from 'components/UI';
import { faCalendarDays } from '@fortawesome/pro-regular-svg-icons';

import { DatePicker } from 'components/UI/V3/DatePicker/DatePicker';

import type { RoomsOperatingReport, RoomsOperatingMetric, RoomsOperatingPeriods } from 'model/RoomsOperatingReport';

type Props = {
    loading: boolean;
    report: RoomsOperatingReport;
    periods: RoomsOperatingPeriods;
    timeZone: string;

    onPeriodChange: (p: Period | null) => void; // (aún no usado aquí)
    onCompareTodayChange: (d: Moment | null) => void;
    onCompareMtdChange: (p: Period | null) => void;
    onCompareYtdChange: (p: Period | null) => void;
};

type MetricKind = 'currency' | 'count' | 'percent' | 'nights';

const fmtCurrency = (n: number) =>
    n.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

const fmtNumber = (n: number) => n.toLocaleString();
const fmtPercent = (n: number) => `${(n * 100).toFixed(0)}%`;

const deltaPct = (current: number, previous: number) => {
    if (previous === 0) return 1;
    return (current - previous) / previous;
};

const valueFor = (m: RoomsOperatingMetric, scope: 'today' | 'mtd' | 'ytd') => {
    const { value } = m[scope];
    switch (m.kind as MetricKind) {
        case 'currency':
            return fmtCurrency(value);
        case 'percent':
            return fmtPercent(value);
        case 'nights':
            return value.toFixed(1);
        default:
            return fmtNumber(value);
    }
};

const lyValueFor = (m: RoomsOperatingMetric, scope: 'today' | 'mtd' | 'ytd') => {
    const { ly } = m[scope];
    switch (m.kind as MetricKind) {
        case 'currency':
            return fmtCurrency(ly);
        case 'percent':
            return fmtPercent(ly);
        case 'nights':
            return ly.toFixed(1);
        default:
            return fmtNumber(ly);
    }
};

const deltaFor = (m: RoomsOperatingMetric, scope: 'today' | 'mtd' | 'ytd') => {
    const { value, ly } = m[scope];
    const pct = deltaPct(value, ly);
    const sign = pct > 0 ? '+' : pct < 0 ? '−' : '';
    const className = pct > 0 ? styles['delta-positive'] : pct < 0 ? styles['delta-negative'] : styles['delta-neutral'];

    return (
        <span className={className}>
            {sign}
            {Math.abs(pct * 100).toFixed(0)}%
        </span>
    );
};

const SECTION_ORDER: RoomsOperatingMetric['section'][] = ['Revenue', 'Pets', 'Rates', 'Rooms', 'Sales'];
const EMPHASIZE_KEYS = new Set<string>(['rev_per_pet', 'inhouse_pets', 'total_rooms_sold']);

// Labels (delegado aquí)
const formatSingleLabel = (d: Moment, tz: string) => d.clone().tz(tz).format('MMM D, YYYY');
const formatRangeLabel = (p: Period, tz: string) =>
    `${p.from.clone().tz(tz).format('MM/DD/YY')} - ${p.to.clone().tz(tz).format('MM/DD/YY')}`;

// Close on click outside
const useClickOutside = (ref: React.RefObject<HTMLElement>, handler: () => void, enabled: boolean) => {
    React.useEffect(() => {
        if (!enabled) return;

        const onDown = (e: MouseEvent) => {
            const el = ref.current;
            if (!el) return;
            if (el.contains(e.target as Node)) return;
            handler();
        };

        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [ref, handler, enabled]);
};

// Range is "complete" when user picked a different end date (2nd click)
const isCompleteRange = (p: Period | null) => {
    if (!p) return false;
    return p.to.diff(p.from, 'days') !== 0;
};

export default function RoomsOperatingTable({
    loading,
    report,
    periods,
    timeZone,
    onCompareTodayChange,
    onCompareMtdChange,
    onCompareYtdChange
}: Props) {
    const metrics = report?.metrics ?? [];

    // Main labels (Today / MTD / YTD)
    const todayLabel = React.useMemo(
        () => formatSingleLabel(periods.period.from, timeZone),
        [periods.period, timeZone]
    );

    const mtdLabel = React.useMemo(() => {
        const end = periods.period.to.clone().tz(timeZone);
        const from = end.clone().startOf('month').startOf('day');
        const to = end.clone().endOf('day');
        return `${from.format('MM/DD/YY')} - ${to.format('MM/DD/YY')}`;
    }, [periods.period, timeZone]);

    const ytdLabel = React.useMemo(() => {
        const end = periods.period.to.clone().tz(timeZone);
        const from = end.clone().startOf('year').startOf('day');
        const to = end.clone().endOf('day');
        return `${from.format('MM/DD/YY')} - ${to.format('MM/DD/YY')}`;
    }, [periods.period, timeZone]);

    // Compare labels
    const todayLyLabel = React.useMemo(
        () => formatSingleLabel(periods.compareToday, timeZone),
        [periods.compareToday, timeZone]
    );
    const mtdLyLabel = React.useMemo(
        () => formatRangeLabel(periods.compareMtd, timeZone),
        [periods.compareMtd, timeZone]
    );
    const ytdLyLabel = React.useMemo(
        () => formatRangeLabel(periods.compareYtd, timeZone),
        [periods.compareYtd, timeZone]
    );

    // Dropdown states
    const [openCompareToday, setOpenCompareToday] = React.useState(false);
    const [openCompareMtd, setOpenCompareMtd] = React.useState(false);
    const [openCompareYtd, setOpenCompareYtd] = React.useState(false);

    // Draft values for range pickers (avoid closing after first click)
    const [draftCompareMtd, setDraftCompareMtd] = React.useState<Period | null>(periods.compareMtd);
    const [draftCompareYtd, setDraftCompareYtd] = React.useState<Period | null>(periods.compareYtd);

    // Keep drafts in sync with parent when popovers are closed
    React.useEffect(() => {
        if (!openCompareMtd) setDraftCompareMtd(periods.compareMtd);
    }, [periods.compareMtd, openCompareMtd]);

    React.useEffect(() => {
        if (!openCompareYtd) setDraftCompareYtd(periods.compareYtd);
    }, [periods.compareYtd, openCompareYtd]);

    // refs for click-outside
    const todayRef = React.useRef<HTMLDivElement>(null);
    const mtdRef = React.useRef<HTMLDivElement>(null);
    const ytdRef = React.useRef<HTMLDivElement>(null);

    useClickOutside(todayRef, () => setOpenCompareToday(false), openCompareToday);
    useClickOutside(mtdRef, () => setOpenCompareMtd(false), openCompareMtd);
    useClickOutside(ytdRef, () => setOpenCompareYtd(false), openCompareYtd);

    // Today (single): close immediately
    const handleCompareTodayPicked = (d: Moment | null) => {
        onCompareTodayChange(d);
        setOpenCompareToday(false);
    };

    // MTD (range): only close+commit when range is complete
    const handleCompareMtdPicked = (p: Period | null) => {
        if (!p) {
            setDraftCompareMtd(null);
            return;
        }

        // update draft on every click (so calendar highlights partial range)
        setDraftCompareMtd(p);

        // commit only after second click
        if (isCompleteRange(p)) {
            onCompareMtdChange(p);
            setOpenCompareMtd(false);
        }
    };

    // YTD (range): only close+commit when range is complete
    const handleCompareYtdPicked = (p: Period | null) => {
        if (!p) {
            setDraftCompareYtd(null);
            return;
        }

        setDraftCompareYtd(p);

        if (isCompleteRange(p)) {
            onCompareYtdChange(p);
            setOpenCompareYtd(false);
        }
    };

    return (
        <div className={styles.container}>
            <Table
                firstColumnFixed
                fullHeight
            >
                <TableHeader>
                    <TableHeaderCell></TableHeaderCell>

                    {/* TODAY vs LY */}
                    <TableHeaderCell>{todayLabel}</TableHeaderCell>

                    <TableHeaderCell className={styles.filterCell}>
                        <div
                            ref={todayRef}
                            className={styles.datePickerCell}
                        >
                            <Label className={styles.compareLabel}>Compare to</Label>

                            <Button
                                label={todayLyLabel}
                                variant="outlined"
                                leftIcon={faCalendarDays}
                                onClick={() => {
                                    setOpenCompareToday(v => !v);
                                    setOpenCompareMtd(false);
                                    setOpenCompareYtd(false);
                                }}
                            />

                            {openCompareToday ? (
                                <div className={styles.datePickerDropdown}>
                                    <DatePicker
                                        rangeMode={false}
                                        timeZone={timeZone}
                                        value={periods.compareToday}
                                        onChange={handleCompareTodayPicked}
                                    />
                                </div>
                            ) : null}
                        </div>
                    </TableHeaderCell>

                    <TableHeaderCell>%</TableHeaderCell>

                    {/* MTD vs LY */}
                    <TableHeaderCell>{mtdLabel}</TableHeaderCell>

                    <TableHeaderCell className={styles.filterCell}>
                        <div
                            ref={mtdRef}
                            className={styles.datePickerCell}
                        >
                            <Label className={styles.compareLabel}>Compare to</Label>

                            <Button
                                label={mtdLyLabel}
                                variant="outlined"
                                leftIcon={faCalendarDays}
                                onClick={() => {
                                    // reset draft to current parent value when opening
                                    setDraftCompareMtd(periods.compareMtd);
                                    setOpenCompareMtd(v => !v);
                                    setOpenCompareToday(false);
                                    setOpenCompareYtd(false);
                                }}
                            />

                            {openCompareMtd ? (
                                <div className={styles.datePickerDropdown}>
                                    <DatePicker
                                        rangeMode
                                        timeZone={timeZone}
                                        value={draftCompareMtd}
                                        onChange={handleCompareMtdPicked}
                                    />
                                </div>
                            ) : null}
                        </div>
                    </TableHeaderCell>

                    <TableHeaderCell>%</TableHeaderCell>

                    {/* YTD vs LY */}
                    <TableHeaderCell>{ytdLabel}</TableHeaderCell>

                    <TableHeaderCell className={styles.filterCell}>
                        <div
                            ref={ytdRef}
                            className={styles.datePickerCell}
                        >
                            <Label className={styles.compareLabel}>Compare to</Label>

                            <Button
                                label={ytdLyLabel}
                                variant="outlined"
                                leftIcon={faCalendarDays}
                                onClick={() => {
                                    setDraftCompareYtd(periods.compareYtd);
                                    setOpenCompareYtd(v => !v);
                                    setOpenCompareToday(false);
                                    setOpenCompareMtd(false);
                                }}
                            />

                            {openCompareYtd ? (
                                <div className={styles.datePickerDropdown}>
                                    <DatePicker
                                        rangeMode
                                        timeZone={timeZone}
                                        value={draftCompareYtd}
                                        onChange={handleCompareYtdPicked}
                                    />
                                </div>
                            ) : null}
                        </div>
                    </TableHeaderCell>

                    <TableHeaderCell>%</TableHeaderCell>
                </TableHeader>

                <TableBody>
                    {loading ? (
                        <TableRow>
                            <TableCell colSpan={10}>
                                <div className={styles.loading}>
                                    <Spinner className={styles.spinner} />
                                </div>
                            </TableCell>
                        </TableRow>
                    ) : (
                        SECTION_ORDER.map(section => {
                            const group = metrics.filter(m => m.section === section);
                            if (!group.length) return null;

                            return (
                                <React.Fragment key={section}>
                                    {group.map((m, idx) => {
                                        const isLast = idx === group.length - 1;
                                        const rowClass = isLast ? styles['row-last-in-section'] : undefined;
                                        const labelClass = EMPHASIZE_KEYS.has(m.key)
                                            ? styles['cell-emphasis']
                                            : undefined;

                                        return (
                                            <TableRow
                                                key={m.key}
                                                className={rowClass}
                                            >
                                                <TableCell className={labelClass}>{m.label}</TableCell>

                                                {/* TODAY vs LY */}
                                                <TableCell>{valueFor(m, 'today')}</TableCell>
                                                <TableCell>{lyValueFor(m, 'today')}</TableCell>
                                                <TableCell>{deltaFor(m, 'today')}</TableCell>

                                                {/* MTD vs LY */}
                                                <TableCell>{valueFor(m, 'mtd')}</TableCell>
                                                <TableCell>{lyValueFor(m, 'mtd')}</TableCell>
                                                <TableCell>{deltaFor(m, 'mtd')}</TableCell>

                                                {/* YTD vs LY */}
                                                <TableCell>{valueFor(m, 'ytd')}</TableCell>
                                                <TableCell>{lyValueFor(m, 'ytd')}</TableCell>
                                                <TableCell>{deltaFor(m, 'ytd')}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })
                    )}
                </TableBody>
            </Table>
        </div>
    );
}
